import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Edit3,
  Eye,
  EyeOff,
  LayoutDashboard,
  LogOut,
  CalendarDays,
  Plus,
  Radio,
  RefreshCw,
  Share2,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../../auth/useAuth";
import EventsIcon from "../../../../assets/logos/Events Icon.png";
import ChurchLogo from "../../../../assets/logos/Church-Logo.png";
import GlobalNotificationPill from "../../../../core/components/GlobalNotificationPill";
import PanelLoadingState from "../../../../core/components/PanelLoadingState";
import useResponsive from "../../../../core/hooks/useResponsive";
import { churchMobileNavStyles } from "../../styles/churchMobileNavStyles";
import { fetchOrganizationAccess } from "../../services/churchOrganizationAccessService";
import {
  createChurchEvent,
  deleteChurchEvent,
  getChurchEvents,
  loadChurchEvents,
  shareChurchEventWithOrganizations,
  toggleChurchEventLive,
  updateChurchEvent,
} from "../../services/churchContentService";
import {
  loadChurchLiveDisplay,
  refreshChurchPreServiceLoop,
} from "../../services/liveDisplayService";
import { supabase } from "../../services/churchSupabaseClient";
import { clearChurchOrgUserSession } from "../../services/churchOrgUserAuthService";

const EVENT_SECTIONS = [
  { id: "overview", label: "Overview", shortLabel: "Home", icon: LayoutDashboard },
  { id: "compose", label: "Compose", shortLabel: "Create", icon: Plus },
  { id: "live", label: "Live Queue", shortLabel: "Live", icon: Radio },
];

const emptyDraft = {
  body: "",
  eventDate: "",
  eventTime: "",
  location: "",
  shareWithOrganizations: false,
  showOnLive: true,
  title: "",
};

function buildEventMeta(item = {}) {
  return [item.eventDate, item.location, item.eventTime].filter(Boolean).join(" • ");
}

function parseEventDate(value = "") {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isEventPassed(item = {}, today = new Date()) {
  const eventDate = parseEventDate(item.eventDate);
  if (!eventDate) return false;

  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return eventDate < todayStart;
}

function getWorkspaceName(user) {
  return (
    user?.churchOrgSession?.account?.name ||
    user?.account?.name ||
    user?.organization?.name ||
    "Church Workspace"
  );
}

function getWorkspaceLogo(user) {
  return (
    user?.churchOrgSession?.account?.logo_url ||
    user?.churchOrgSession?.account?.logoUrl ||
    user?.account?.logo_url ||
    user?.account?.logoUrl ||
    user?.organization?.logo_url ||
    user?.organization?.logoUrl ||
    ChurchLogo
  );
}

export default function EventsPage({ useStandaloneRoutes = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPhone } = useResponsive();
  const [churchAccount, setChurchAccount] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const [events, setEvents] = useState(getChurchEvents());
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const resolvedChurchAccount =
    churchAccount ||
    user?.churchOrgSession?.account ||
    user?.account ||
    user?.organization ||
    null;
  const workspaceName = resolvedChurchAccount?.name || getWorkspaceName(user);
  const organizationLogoUrl =
    resolvedChurchAccount?.logo_url ||
    resolvedChurchAccount?.logoUrl ||
    getWorkspaceLogo(user);
  const currentEvents = events.filter((item) => !isEventPassed(item));
  const passedEvents = events.filter((item) => isEventPassed(item));
  const liveEvents = currentEvents.filter((item) => item.showOnLive !== false);
  const hiddenEvents = currentEvents.filter((item) => item.showOnLive === false);
  const selectedEditingItem = events.find((item) => item.id === editingId) || null;

  const previewItems = useMemo(
    () => liveEvents.slice(0, 4),
    [liveEvents]
  );

  async function refreshEvents(options = {}) {
    if (options.showLoading !== false) {
      setLoading(true);
    }
    setError("");

    try {
      const nextEvents = await loadChurchEvents(user?.id);
      setEvents(nextEvents);
      if (options.showNotice) {
        setNotice("Events refreshed.");
      }
    } catch (loadError) {
      console.error("Church events load error:", loadError);
      setError(loadError?.message || "Could not load events.");
    } finally {
      if (options.showLoading !== false) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    let isMounted = true;

    async function loadChurchAccount() {
      if (!user?.id) {
        setChurchAccount(null);
        return;
      }

      const access = await fetchOrganizationAccess(user.id, "church").catch(() => null);
      if (isMounted) {
        setChurchAccount(access?.account || null);
      }
    }

    async function hydrateEvents() {
      setLoading(true);
      try {
        const nextEvents = await loadChurchEvents(user?.id);
        if (isMounted) {
          setEvents(nextEvents);
        }
      } catch (loadError) {
        console.error("Church events load error:", loadError);
        if (isMounted) {
          setError(loadError?.message || "Could not load events.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadChurchAccount();
    hydrateEvents();

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  async function syncLiveLoop(successMessage = "Live display event slide updated.") {
    if (!user?.id) return;

    try {
      const current = await loadChurchLiveDisplay(user.id);

      if (current?.display) {
        await refreshChurchPreServiceLoop(user.id, current.display);
      }
      setNotice(successMessage);
    } catch (loopError) {
      console.error("Event live loop refresh error:", loopError);
      setError(loopError?.message || "Saved, but the Live Display loop could not refresh.");
    }
  }

  function resetDraft() {
    setDraft(emptyDraft);
    setEditingId("");
  }

  function startEditing(item) {
    setEditingId(item.id);
    setDraft({
      body: item.body || "",
      eventDate: item.eventDate || "",
      eventTime: item.eventTime || "",
      location: item.location || "",
      shareWithOrganizations: item.sharedWithOikosChurches === true,
      showOnLive: item.showOnLive !== false,
      title: item.title || "",
    });
    setActiveSection("compose");
  }

  async function handleSaveEvent() {
    if (!draft.title.trim() || busyAction) return;

    const actionKey = editingId ? "edit" : "create";
    const originalEditingId = editingId;
    setBusyAction(actionKey);
    setError("");
    setNotice("");

    try {
      const nextEvents = editingId
        ? await updateChurchEvent(user?.id, editingId, {
            title: draft.title.trim(),
            eventDate: draft.eventDate,
            location: draft.location.trim(),
            eventTime: draft.eventTime.trim(),
            body: draft.body.trim(),
            showOnLive: draft.showOnLive,
          })
        : await createChurchEvent(user?.id, {
            title: draft.title.trim(),
            eventDate: draft.eventDate,
            location: draft.location.trim(),
            eventTime: draft.eventTime.trim(),
            body: draft.body.trim(),
            showOnLive: draft.showOnLive,
            sharedWithOikosChurches: draft.shareWithOrganizations,
            type: "event",
          });

      const savedEvent =
        nextEvents.find((item) => item.id === originalEditingId) ||
        nextEvents.find(
          (item) =>
            item.title === draft.title.trim() &&
            item.eventDate === draft.eventDate &&
            item.location === draft.location.trim() &&
            item.eventTime === draft.eventTime.trim()
        );

      setEvents(nextEvents);
      resetDraft();
      setActiveSection("live");
      if (draft.shareWithOrganizations && savedEvent?.id && !savedEvent.sharedWithOikosChurches) {
        await shareChurchEventWithOrganizations(user, savedEvent.id);
        const sharedEvents = await loadChurchEvents(user?.id);
        setEvents(sharedEvents);
      }
      await syncLiveLoop(originalEditingId ? "Event updated." : "Event created.");
    } catch (saveError) {
      console.error("Church event save error:", saveError);
      setError(saveError?.message || "Could not save event.");
    } finally {
      setBusyAction("");
    }
  }

  async function handleRemoveEvent(item) {
    if (!item?.id || busyAction) return;

    const shouldDelete = window.confirm(`Remove "${item.title || "this event"}"?`);
    if (!shouldDelete) return;

    setBusyAction(`delete-${item.id}`);
    setError("");
    setNotice("");

    try {
      const nextEvents = await deleteChurchEvent(user?.id, item.id);
      setEvents(nextEvents);
      if (editingId === item.id) resetDraft();
      await syncLiveLoop("Event removed.");
    } catch (deleteError) {
      console.error("Church event delete error:", deleteError);
      setError(deleteError?.message || "Could not remove event.");
    } finally {
      setBusyAction("");
    }
  }

  async function handleToggleShowOnLive(item) {
    if (!item?.id || busyAction) return;

    setBusyAction(`live-${item.id}`);
    setError("");
    setNotice("");

    try {
      const nextEvents = await toggleChurchEventLive(user?.id, item);
      setEvents(nextEvents);
      await syncLiveLoop(item.showOnLive === false ? "Event added to Live Display." : "Event hidden from Live Display.");
    } catch (toggleError) {
      console.error("Church event live toggle error:", toggleError);
      setError(toggleError?.message || "Could not update Live Display visibility.");
    } finally {
      setBusyAction("");
    }
  }

  async function handleShareEvent(item) {
    if (!item?.id || busyAction) return;

    const shouldShare = window.confirm(
      `Share "${item.title || "this event"}" with other churches in your area?`
    );
    if (!shouldShare) return;

    setBusyAction(`share-${item.id}`);
    setError("");
    setNotice("");

    try {
      const result = await shareChurchEventWithOrganizations(user, item.id);
      const nextEvents = await loadChurchEvents(user?.id);
      setEvents(nextEvents);
      await syncLiveLoop("Event shared with other organizations.");
      const notificationCount = Number(result?.notificationCount || result?.notification_count || 0);
      setNotice(
        notificationCount > 0
          ? `Event shared and ${notificationCount} Church Contact notification${notificationCount === 1 ? "" : "s"} sent.`
          : "Event shared with other organizations."
      );
    } catch (shareError) {
      console.error("Church event share error:", shareError);
      setError(shareError?.message || "Could not share this event.");
    } finally {
      setBusyAction("");
    }
  }

  const showOverview = !useStandaloneRoutes || activeSection === "overview";
  const showCompose = !useStandaloneRoutes || activeSection === "compose";
  const showLive = !useStandaloneRoutes || activeSection === "live";

  async function handleLogout() {
    clearChurchOrgUserSession();
    await supabase.auth.signOut();
    navigate("/church/login", { replace: true });
  }

  if (loading) {
    return (
      <PanelLoadingState
        iconAlt="Events"
        iconSrc={EventsIcon}
        title="Loading Events"
        detail="Preparing event cards, live visibility, and display updates..."
      />
    );
  }

  return (
    <div style={useStandaloneRoutes ? { ...styles.wrapper, ...(isPhone ? styles.wrapperPhone : {}) } : styles.page}>
      <div
        style={
          useStandaloneRoutes
            ? { ...styles.managementHeader, ...(isPhone ? styles.managementHeaderPhone : {}) }
            : { ...styles.pageHeader, ...(isPhone ? styles.pageHeaderPhone : {}) }
        }
      >
        {useStandaloneRoutes ? (
          <>
            <span aria-hidden="true" style={styles.managementHeaderShapeOne} />
            <span aria-hidden="true" style={styles.managementHeaderShapeTwo} />
            <div style={{ ...styles.managementHeaderMain, ...(isPhone ? styles.managementHeaderMainPhone : {}) }}>
              <img src={EventsIcon} alt="Events" style={styles.managementHeaderIcon} />
              <div style={styles.managementHeaderCopy}>
                <div style={styles.managementHeaderEyebrow}>Oikos Church Events</div>
                <div style={styles.managementHeaderName}>{workspaceName}</div>
              </div>
            </div>
            <div style={{ ...styles.managementHeaderMeta, ...(isPhone ? styles.managementHeaderMetaPhone : {}) }}>
              <div style={styles.managementHeaderWelcomeLabel}>Display</div>
                <div style={styles.managementHeaderUserName}>{liveEvents.length} shown / {passedEvents.length} passed</div>
              <button
                type="button"
                style={{ ...styles.managementHeaderIdRow, ...(isPhone ? styles.managementHeaderIdRowPhone : {}) }}
                onClick={() => syncLiveLoop("Live Display events refreshed.")}
                disabled={Boolean(busyAction)}
              >
                <RefreshCw size={15} />
                <span>Refresh Live Display</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <div>
              <h2 style={styles.pageTitle}>Events</h2>
              <div style={styles.pageMeta}>{liveEvents.length} shown on Live Display / {passedEvents.length} passed</div>
            </div>
            <button
              style={styles.refreshButton}
              onClick={() => syncLiveLoop("Live Display events refreshed.")}
              disabled={Boolean(busyAction)}
            >
              Refresh Live Display
            </button>
          </>
        )}
      </div>

      <GlobalNotificationPill
        message={error || notice}
        onDismiss={() => {
          if (error) {
            setError("");
          } else {
            setNotice("");
          }
        }}
        tone={error ? "error" : "success"}
      />

      <div style={useStandaloneRoutes ? styles.eventShell : styles.embeddedShell}>
        {useStandaloneRoutes ? (
          <aside style={{ ...styles.sideNav, ...(isPhone ? styles.sideNavPhone : {}) }} aria-label="Event sections">
            <div style={{ ...styles.sideNavBrand, ...(isPhone ? styles.sideNavBrandPhone : {}) }}>
              <img src={ChurchLogo} alt="Oikos Church" style={styles.sideNavBrandLogo} />
              <div style={styles.sideNavBrandLine} />
            </div>
            <div style={{ ...styles.sideNavOrg, ...(isPhone ? styles.sideNavOrgPhone : {}) }}>
              <img src={organizationLogoUrl} alt={workspaceName} style={styles.sideNavLogo} />
              <div style={styles.sideNavOrgCopy}>
                <span style={styles.sideNavOrgLabel}>Organization</span>
                <strong style={styles.sideNavOrgName}>{workspaceName}</strong>
              </div>
            </div>
            <div style={{ ...styles.sideNavDivider, ...(isPhone ? styles.sideNavDividerPhone : {}) }} />
            <div style={{ ...styles.sideNavTitle, ...(isPhone ? styles.sideNavTitlePhone : {}) }}>
              Events
            </div>
            <div style={{ ...styles.navMain, ...(isPhone ? styles.navMainPhone : {}) }}>
              {EVENT_SECTIONS.map((section) => {
                const Icon = section.icon;
                const active = activeSection === section.id;

                return (
                  <button
                    key={section.id}
                    type="button"
                    style={{
                      ...styles.navButton,
                      ...(isPhone ? styles.navButtonPhone : {}),
                      ...(active ? { ...styles.navButtonActive, background: "var(--color-primary)" } : {}),
                      ...(isPhone && active ? styles.navButtonActivePhone : {}),
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.currentTarget.blur();
                      setActiveSection(section.id);
                    }}
                  >
                    <Icon size={isPhone ? 15 : 17} />
                    <span style={isPhone ? styles.navLabelPhone : null}>
                      {isPhone ? section.shortLabel || section.label : section.label}
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{ ...styles.sideNavFooter, ...(isPhone ? styles.sideNavFooterPhone : {}) }}>
              <button
                type="button"
                style={{ ...styles.footerButton, ...(isPhone ? styles.footerButtonPhone : {}) }}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.currentTarget.blur();
                  navigate("/church");
                }}
              >
                <ArrowLeft size={isPhone ? 15 : 16} />
                <span style={isPhone ? styles.navLabelPhone : null}>Apps</span>
              </button>
              <button
                type="button"
                style={{ ...styles.footerButton, ...styles.logoutButton, ...(isPhone ? styles.footerButtonPhone : {}) }}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.currentTarget.blur();
                  handleLogout();
                }}
              >
                <LogOut size={isPhone ? 15 : 16} />
                <span style={isPhone ? styles.navLabelPhone : null}>Logout</span>
              </button>
            </div>
          </aside>
        ) : null}

        <div style={useStandaloneRoutes ? { ...styles.content, ...(isPhone ? styles.contentPhone : {}) } : styles.embeddedContent}>
          {showOverview ? (
            <section style={styles.sectionGrid}>
              <div style={{ ...styles.heroPanel, ...(isPhone ? styles.heroPanelPhone : {}) }}>
                <div>
                  <div style={styles.kicker}>Church Events</div>
                  <h1 style={styles.heroTitle}>Add events for your congregation.</h1>
                  <p style={styles.heroText}>
                    Add event dates, times, locations, and details, then choose which cards show in the pre-service Live Display loop.
                  </p>
                </div>
                <button
                  type="button"
                  style={{ ...styles.primaryButton, ...(isPhone ? styles.primaryButtonPhone : {}) }}
                  onClick={() => setActiveSection("compose")}
                >
                  <Plus size={17} />
                  Add Event
                </button>
              </div>
              <div style={styles.statGrid}>
                <StatCard label="Upcoming Cards" value={currentEvents.length} />
                <StatCard label="Shown Live" value={liveEvents.length} tone="green" />
                <StatCard label="Passed Events" value={passedEvents.length} tone="gray" />
              </div>
              <div style={styles.panel}>
                <div style={styles.panelHeader}>
                  <div>
                    <div style={styles.panelTitle}>Live Preview</div>
                    <div style={styles.panelSubtle}>First cards visible on the event slide</div>
                  </div>
                  <button type="button" style={styles.ghostButton} onClick={() => setActiveSection("live")}>
                    View Queue
                  </button>
                </div>
                <EventList
                  emptyText="No events are currently shown on Live Display."
                  items={previewItems}
                  busyAction={busyAction}
                  onEdit={startEditing}
                  onRemove={handleRemoveEvent}
                  onShare={handleShareEvent}
                  onToggleLive={handleToggleShowOnLive}
                />
              </div>
            </section>
          ) : null}

          {showCompose ? (
            <section style={styles.sectionGrid}>
              <div style={styles.panel}>
                <div style={styles.panelHeader}>
                  <div>
                    <div style={styles.panelTitle}>{editingId ? "Edit Event" : "Create Event"}</div>
                    <div style={styles.panelSubtle}>{selectedEditingItem ? selectedEditingItem.title : "Draft a card for the events slide"}</div>
                  </div>
                  {editingId ? (
                    <button type="button" style={styles.ghostButton} onClick={resetDraft}>
                      Cancel Edit
                    </button>
                  ) : null}
                </div>
                <div style={styles.formGrid}>
                  <label style={styles.field}>
                    <span style={styles.fieldLabel}>Title</span>
                    <input
                      style={styles.input}
                      placeholder="Event title"
                      value={draft.title}
                      onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
                    />
                  </label>
                  <div style={{ ...styles.eventDetailGrid, ...(isPhone ? styles.eventDetailGridPhone : {}) }}>
                    <label style={styles.field}>
                      <span style={styles.fieldLabel}>Date</span>
                      <input
                        type="date"
                        style={styles.input}
                        value={draft.eventDate}
                        onChange={(event) => setDraft((current) => ({ ...current, eventDate: event.target.value }))}
                      />
                    </label>
                    <label style={styles.field}>
                      <span style={styles.fieldLabel}>Location</span>
                      <input
                        style={styles.input}
                        placeholder="Fellowship Hall"
                        value={draft.location}
                        onChange={(event) => setDraft((current) => ({ ...current, location: event.target.value }))}
                      />
                    </label>
                    <label style={styles.field}>
                      <span style={styles.fieldLabel}>Time</span>
                      <input
                        style={styles.input}
                        placeholder="Sunday at 12:30 PM"
                        value={draft.eventTime}
                        onChange={(event) => setDraft((current) => ({ ...current, eventTime: event.target.value }))}
                      />
                    </label>
                  </div>
                  <label style={styles.field}>
                    <span style={styles.fieldLabel}>Message</span>
                    <textarea
                      style={styles.textarea}
                      placeholder="Write the event message"
                      value={draft.body}
                      onChange={(event) => setDraft((current) => ({ ...current, body: event.target.value }))}
                    />
                  </label>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={draft.showOnLive}
                    style={{
                      ...styles.toggleRow,
                      ...(draft.showOnLive ? styles.toggleRowActive : {}),
                    }}
                    onClick={() => setDraft((current) => ({ ...current, showOnLive: !current.showOnLive }))}
                  >
                    <span style={styles.toggleCopy}>
                      <span style={styles.toggleTitle}>Show on Live Display</span>
                      <span style={styles.toggleDetail}>
                        {draft.showOnLive ? "Included on the event slide" : "Hidden from the event slide"}
                      </span>
                    </span>
                    <span
                      aria-hidden="true"
                      style={{
                        ...styles.toggleTrack,
                        ...(draft.showOnLive ? styles.toggleTrackOn : {}),
                      }}
                    >
                      <span
                        style={{
                          ...styles.toggleKnob,
                          ...(draft.showOnLive ? styles.toggleKnobOn : {}),
                        }}
                      />
                    </span>
                  </button>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={draft.shareWithOrganizations}
                    style={{
                      ...styles.toggleRow,
                      ...(draft.shareWithOrganizations ? styles.toggleRowActive : {}),
                    }}
                    onClick={() =>
                      setDraft((current) => ({
                        ...current,
                        shareWithOrganizations: !current.shareWithOrganizations,
                      }))
                    }
                  >
                    <span style={styles.toggleCopy}>
                      <span style={styles.toggleTitle}>Share with other churches in your area</span>
                      <span style={styles.toggleDetail}>
                        {draft.shareWithOrganizations ? "Other organizations can see this event" : "Only your organization can see this event"}
                      </span>
                    </span>
                    <span
                      aria-hidden="true"
                      style={{
                        ...styles.toggleTrack,
                        ...(draft.shareWithOrganizations ? styles.toggleTrackOn : {}),
                      }}
                    >
                      <span
                        style={{
                          ...styles.toggleKnob,
                          ...(draft.shareWithOrganizations ? styles.toggleKnobOn : {}),
                        }}
                      />
                    </span>
                  </button>
                  <button
                    type="button"
                    style={{
                      ...styles.primaryButton,
                      ...styles.formSubmitButton,
                      ...(!draft.title.trim() || busyAction ? styles.disabledButton : {}),
                    }}
                    onClick={handleSaveEvent}
                    disabled={!draft.title.trim() || Boolean(busyAction)}
                  >
                    <CalendarDays size={17} />
                    {busyAction === "create" || busyAction === "edit" ? "Saving..." : editingId ? "Save Changes" : "Add Event"}
                  </button>
                </div>
              </div>
            </section>
          ) : null}

          {showLive ? (
            <section style={styles.sectionGrid}>
              <div style={styles.panel}>
                <div style={styles.panelHeader}>
                  <div>
                    <div style={styles.panelTitle}>Live Queue</div>
                    <div style={styles.panelSubtle}>Upcoming events with Live Display visibility</div>
                  </div>
                  <button type="button" style={styles.ghostButton} onClick={() => refreshEvents({ showLoading: false, showNotice: true })}>
                    <RefreshCw size={15} />
                    Refresh
                  </button>
                </div>
                <EventList
                  emptyText="No events have been created yet."
                  items={currentEvents}
                  busyAction={busyAction}
                  onEdit={startEditing}
                  onRemove={handleRemoveEvent}
                  onShare={handleShareEvent}
                  onToggleLive={handleToggleShowOnLive}
                />
              </div>
              <div style={styles.panel}>
                <div style={styles.panelHeader}>
                  <div>
                    <div style={styles.panelTitle}>Passed Events</div>
                    <div style={styles.panelSubtle}>Saved events that have already happened</div>
                  </div>
                </div>
                <EventList
                  emptyText="No passed events yet."
                  items={passedEvents}
                  busyAction={busyAction}
                  onEdit={startEditing}
                  onRemove={handleRemoveEvent}
                  onShare={handleShareEvent}
                  onToggleLive={handleToggleShowOnLive}
                  passed
                />
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone = "blue" }) {
  return (
    <div style={{ ...styles.statCard, ...(tone === "green" ? styles.statGreen : tone === "gray" ? styles.statGray : {}) }}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statValue}>{value}</div>
    </div>
  );
}

function EventList({ busyAction, emptyText, items, onEdit, onRemove, onShare, onToggleLive, passed = false }) {
  if (!items.length) {
    return <div style={styles.emptyState}>{emptyText}</div>;
  }

  return (
    <div style={styles.cardList}>
      {items.map((item) => {
        const isLive = item.showOnLive !== false;
        const isBusy = busyAction === `live-${item.id}` || busyAction === `delete-${item.id}` || busyAction === `share-${item.id}`;
        const eventMeta = buildEventMeta(item);

        return (
          <article key={item.id} style={{ ...styles.eventCard, ...(passed ? styles.eventCardPassed : {}) }}>
            <div style={styles.eventMain}>
              <div style={{ ...styles.statusDot, ...(passed ? styles.statusDotPassed : isLive ? styles.statusDotLive : styles.statusDotHidden) }} />
              <div style={styles.eventCopy}>
                <div style={styles.eventTitleRow}>
                  <div style={styles.eventTitle}>{item.title || "Church Event"}</div>
                  {passed ? <span style={styles.passedBadge}>Passed</span> : null}
                  {item.sharedWithOikosChurches ? <span style={styles.sharedBadge}>Shared</span> : null}
                </div>
                {eventMeta ? <div style={styles.eventMeta}>{eventMeta}</div> : null}
                <div style={styles.eventText}>{item.body || "No message added."}</div>
              </div>
            </div>
            <div style={styles.cardActions}>
              {passed ? null : (
                <button
                  type="button"
                  style={{
                    ...styles.iconButton,
                    ...(isLive ? styles.liveButtonOn : styles.liveButtonOff),
                  }}
                  onClick={() => onToggleLive(item)}
                  disabled={isBusy}
                  title={isLive ? "Hide from Live Display" : "Show on Live Display"}
                >
                  {isLive ? <Eye size={15} /> : <EyeOff size={15} />}
                  <span>{isLive ? "Live" : "Hidden"}</span>
                </button>
              )}
              {passed ? null : (
                <button
                  type="button"
                  style={{
                    ...styles.iconButton,
                    ...(item.sharedWithOikosChurches ? styles.sharedButtonOn : styles.shareButton),
                  }}
                  onClick={() => onShare(item)}
                  disabled={isBusy}
                  title="Share with other churches in your area"
                >
                  <Share2 size={15} />
                  <span>{item.sharedWithOikosChurches ? "Shared" : "Share"}</span>
                </button>
              )}
              <button
                type="button"
                style={styles.iconButton}
                onClick={() => onEdit(item)}
                disabled={isBusy}
              >
                <Edit3 size={15} />
                <span>Edit</span>
              </button>
              <button
                type="button"
                style={{
                  ...styles.iconButton,
                  ...styles.dangerButton,
                }}
                onClick={() => onRemove(item)}
                disabled={isBusy}
              >
                <Trash2 size={15} />
                <span>Remove</span>
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

export const eventPageStyles = {
  wrapper: {
    color: "#0f172a",
    minHeight: "100%",
    padding: "10px 0 28px",
    position: "relative",
  },
  wrapperPhone: {
    padding: "4px 0 calc(94px + env(safe-area-inset-bottom))",
  },
  page: {
    display: "grid",
    gap: 16,
    padding: 12,
  },
  pageHeader: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d8e4dd",
    borderRadius: 18,
    display: "flex",
    justifyContent: "space-between",
    padding: 16,
  },
  pageHeaderPhone: {
    alignItems: "stretch",
    flexDirection: "column",
    gap: 12,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 950,
    margin: 0,
  },
  pageMeta: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 750,
  },
  managementHeader: {
    alignItems: "center",
    background:
      "radial-gradient(circle at 16% 18%, rgba(var(--color-primary-rgb),0.13), transparent 28%), radial-gradient(circle at 86% 0%, rgba(216,229,208,0.88), transparent 30%), linear-gradient(135deg, rgba(255,255,255,0.96), rgba(248,250,252,0.91))",
    backdropFilter: "blur(16px) saturate(1.08)",
    WebkitBackdropFilter: "blur(16px) saturate(1.08)",
    border: "1px solid #d8e5d0",
    borderRadius: 18,
    boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
    display: "flex",
    gap: 16,
    justifyContent: "space-between",
    margin: "0 20px 18px 296px",
    minHeight: 108,
    overflow: "hidden",
    padding: "16px 18px",
    position: "sticky",
    top: 0,
    zIndex: 70,
  },
  managementHeaderPhone: {
    alignItems: "flex-start",
    flexDirection: "column",
    gap: 12,
    margin: "0 0 14px",
    minHeight: 0,
    top: 4,
    padding: 16,
  },
  managementHeaderShapeOne: {
    background: "rgba(var(--color-primary-rgb),0.08)",
    border: "1px solid rgba(var(--color-primary-rgb),0.08)",
    borderRadius: 999,
    height: 132,
    left: "38%",
    position: "absolute",
    top: -72,
    transform: "rotate(-18deg)",
    width: 260,
    zIndex: 0,
  },
  managementHeaderShapeTwo: {
    background: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(95,125,77,0.10)",
    borderRadius: 999,
    bottom: -64,
    height: 110,
    position: "absolute",
    right: 28,
    transform: "rotate(14deg)",
    width: 210,
    zIndex: 0,
  },
  managementHeaderMain: {
    alignItems: "center",
    display: "flex",
    gap: 16,
    minWidth: 0,
    position: "relative",
    zIndex: 1,
  },
  managementHeaderMainPhone: {
    alignItems: "flex-start",
  },
  managementHeaderIcon: {
    display: "block",
    flex: "0 0 auto",
    height: 74,
    objectFit: "contain",
    width: 74,
  },
  managementHeaderCopy: {
    minWidth: 0,
  },
  managementHeaderEyebrow: {
    color: "var(--color-primary-dark)",
    fontSize: 13,
    fontWeight: 950,
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  managementHeaderName: {
    color: "#0f172a",
    fontSize: 28,
    fontWeight: 950,
    lineHeight: 1.08,
    marginTop: 5,
    overflowWrap: "anywhere",
  },
  managementHeaderChurchName: {
    color: "#4b5563",
    fontSize: 14,
    fontWeight: 800,
    marginTop: 4,
  },
  managementHeaderMeta: {
    alignItems: "flex-end",
    display: "flex",
    flex: "0 0 auto",
    flexDirection: "column",
    gap: 7,
    maxWidth: 300,
    position: "relative",
    textAlign: "right",
    zIndex: 1,
  },
  managementHeaderMetaPhone: {
    alignItems: "flex-start",
    maxWidth: "100%",
    textAlign: "left",
    width: "100%",
  },
  managementHeaderWelcomeLabel: {
    color: "var(--color-primary-dark)",
    fontSize: 11,
    fontWeight: 950,
    textTransform: "uppercase",
  },
  managementHeaderUserName: {
    color: "#0f172a",
    fontSize: 22,
    fontWeight: 950,
    lineHeight: 1.1,
    overflowWrap: "anywhere",
  },
  managementHeaderIdRow: {
    alignItems: "center",
    background: "rgba(255,255,255,0.7)",
    border: "1px solid rgba(95,125,77,0.14)",
    borderRadius: 999,
    color: "var(--color-primary-dark)",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 13,
    fontWeight: 900,
    gap: 8,
    justifyContent: "flex-end",
    maxWidth: "100%",
    minWidth: 0,
    padding: "10px 12px",
  },
  managementHeaderIdRowPhone: {
    alignSelf: "flex-start",
    flexWrap: "wrap",
    justifyContent: "flex-start",
  },
  eventShell: {
    display: "block",
  },
  embeddedShell: {
    display: "block",
  },
  content: {
    marginLeft: 296,
    padding: "0 20px 26px 0",
  },
  contentPhone: {
    marginLeft: 0,
    padding: "0 12px 18px",
  },
  embeddedContent: {
    display: "grid",
    gap: 16,
  },
  sideNav: {
    background: "rgba(255,255,255,0.76)",
    backdropFilter: "blur(18px) saturate(1.12)",
    WebkitBackdropFilter: "blur(18px) saturate(1.12)",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.58)",
    borderRadius: 24,
    bottom: 16,
    boxShadow: "0 18px 46px rgba(15,23,42,0.14)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    left: 16,
    padding: 12,
    position: "fixed",
    top: 0,
    width: 252,
    zIndex: 90,
  },
  sideNavPhone: {
    ...churchMobileNavStyles.dock,
    gridAutoRows: "minmax(54px, auto)",
    gridTemplateColumns: `repeat(${EVENT_SECTIONS.length + 2}, minmax(0, 1fr))`,
  },
  sideNavTitle: {
    color: "var(--color-primary-dark)",
    fontSize: 13,
    fontWeight: 900,
    lineHeight: 1.2,
    padding: "8px 12px 4px",
  },
  sideNavTitlePhone: {
    display: "none",
  },
  sideNavBrand: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "6px 8px 8px",
  },
  sideNavBrandPhone: {
    display: "none",
  },
  sideNavBrandLogo: {
    display: "block",
    height: "auto",
    maxHeight: 92,
    maxWidth: 196,
    objectFit: "contain",
    width: "90%",
  },
  sideNavBrandLine: {
    background: "linear-gradient(90deg, transparent, rgba(var(--color-primary-rgb),0.28), transparent)",
    height: 1,
    width: "100%",
  },
  sideNavOrg: {
    alignItems: "center",
    background: "rgba(255,255,255,0.78)",
    border: "1px solid rgba(var(--color-primary-rgb),0.18)",
    borderRadius: 18,
    boxShadow: "0 10px 22px rgba(15,23,42,0.08)",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    margin: "0 0 6px",
    minWidth: 0,
    padding: "18px 14px 16px",
    textAlign: "center",
  },
  sideNavOrgPhone: {
    display: "none",
  },
  sideNavLogo: {
    display: "block",
    height: "auto",
    maxHeight: 146,
    maxWidth: 212,
    objectFit: "contain",
    width: "100%",
  },
  sideNavOrgCopy: {
    minWidth: 0,
    width: "100%",
  },
  sideNavOrgLabel: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  sideNavOrgName: {
    color: "#0f172a",
    display: "block",
    fontSize: 15,
    fontWeight: 800,
    lineHeight: 1.18,
    overflowWrap: "anywhere",
    whiteSpace: "normal",
  },
  sideNavDivider: {
    background: "linear-gradient(90deg, transparent, rgba(var(--color-primary-rgb),0.24), transparent)",
    height: 1,
    margin: "2px 8px 4px",
  },
  sideNavDividerPhone: {
    display: "none",
  },
  navMain: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: 8,
    minHeight: 0,
    overflow: "auto",
  },
  navMainPhone: {
    display: "contents",
  },
  navButton: {
    alignItems: "center",
    background: "rgba(var(--color-primary-rgb),0.09)",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "transparent",
    borderRadius: 999,
    color: "var(--color-primary-dark)",
    cursor: "pointer",
    display: "flex",
    font: "inherit",
    fontSize: 13,
    fontWeight: 850,
    gap: 9,
    justifyContent: "flex-start",
    minHeight: 42,
    outline: "none",
    padding: "0 13px",
    textAlign: "left",
    width: "100%",
  },
  navButtonPhone: {
    ...churchMobileNavStyles.item,
  },
  navButtonActive: {
    color: "#ffffff",
    boxShadow: "none",
  },
  navButtonActivePhone: {
    ...churchMobileNavStyles.activeItem,
  },
  navLabelPhone: {
    ...churchMobileNavStyles.label,
  },
  sideNavFooter: {
    borderTop: "1px solid rgba(var(--color-primary-rgb),0.14)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: "auto",
    paddingTop: 10,
  },
  sideNavFooterPhone: {
    display: "contents",
  },
  footerButton: {
    alignItems: "center",
    background: "rgba(var(--color-primary-rgb),0.12)",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "transparent",
    borderRadius: 999,
    color: "var(--color-primary-dark)",
    cursor: "pointer",
    display: "flex",
    font: "inherit",
    fontSize: 13,
    fontWeight: 850,
    gap: 9,
    minHeight: 42,
    outline: "none",
    padding: "0 13px",
    textAlign: "left",
    width: "100%",
  },
  footerButtonPhone: {
    ...churchMobileNavStyles.item,
  },
  logoutButton: {
    background: "rgba(15,23,42,0.05)",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "transparent",
    color: "#334155",
  },
  sectionGrid: {
    display: "grid",
    gap: 16,
  },
  heroPanel: {
    alignItems: "end",
    background: "#ffffff",
    border: "1px solid #dbe5dd",
    borderRadius: 22,
    boxShadow: "0 18px 46px rgba(15,23,42,0.08)",
    display: "grid",
    gap: 18,
    gridTemplateColumns: "minmax(0, 1fr) auto",
    padding: "clamp(18px, 3vw, 28px)",
  },
  heroPanelPhone: {
    alignItems: "stretch",
    display: "flex",
    flexDirection: "column",
    minHeight: 280,
  },
  kicker: {
    color: "var(--color-primary-dark)",
    fontSize: 13,
    fontWeight: 950,
    textTransform: "uppercase",
  },
  heroTitle: {
    color: "#0f172a",
    fontSize: "clamp(28px, 4vw, 46px)",
    fontWeight: 950,
    lineHeight: 1.03,
    margin: "6px 0",
    maxWidth: 860,
  },
  heroText: {
    color: "#475569",
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.5,
    margin: 0,
    maxWidth: 760,
  },
  statGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  },
  statCard: {
    background: "#eef5ff",
    border: "1px solid #d7e6fb",
    borderRadius: 18,
    padding: 16,
  },
  statGreen: {
    background: "#ecf7ef",
    borderColor: "#cfe8d5",
  },
  statGray: {
    background: "#f8fafc",
    borderColor: "#e2e8f0",
  },
  statLabel: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  statValue: {
    color: "#0f172a",
    fontSize: 34,
    fontWeight: 950,
    lineHeight: 1,
    marginTop: 8,
  },
  panel: {
    background: "#ffffff",
    border: "1px solid #dbe5dd",
    borderRadius: 22,
    boxShadow: "0 18px 46px rgba(15,23,42,0.08)",
    display: "grid",
    gap: 16,
    padding: "clamp(16px, 2.6vw, 24px)",
  },
  panelHeader: {
    alignItems: "center",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  panelTitle: {
    color: "#0f172a",
    fontSize: 22,
    fontWeight: 950,
    lineHeight: 1.1,
  },
  panelSubtle: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 750,
    marginTop: 3,
  },
  formGrid: {
    display: "grid",
    gap: 14,
    width: "100%",
  },
  eventDetailGrid: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "minmax(0, 0.8fr) minmax(0, 1fr) minmax(0, 0.8fr)",
  },
  eventDetailGridPhone: {
    gridTemplateColumns: "1fr",
  },
  field: {
    display: "grid",
    gap: 6,
  },
  fieldLabel: {
    color: "#334155",
    fontSize: 13,
    fontWeight: 900,
  },
  input: {
    background: "#f8fafc",
    border: "1px solid #d7e3dc",
    borderRadius: 14,
    boxSizing: "border-box",
    fontSize: 15,
    fontWeight: 750,
    outline: "none",
    padding: "13px 14px",
    width: "100%",
  },
  textarea: {
    background: "#f8fafc",
    border: "1px solid #d7e3dc",
    borderRadius: 14,
    boxSizing: "border-box",
    fontFamily: "inherit",
    fontSize: 15,
    fontWeight: 700,
    minHeight: "clamp(180px, 24vh, 280px)",
    outline: "none",
    padding: "13px 14px",
    resize: "vertical",
    width: "100%",
  },
  toggleRow: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #d7e3dc",
    borderRadius: 14,
    color: "#334155",
    cursor: "pointer",
    display: "flex",
    fontSize: 14,
    gap: 14,
    justifyContent: "space-between",
    minHeight: 64,
    padding: "13px 14px",
    textAlign: "left",
    width: "100%",
  },
  toggleRowActive: {
    background: "rgba(var(--color-primary-rgb), 0.1)",
    borderColor: "rgba(var(--color-primary-rgb), 0.3)",
  },
  toggleCopy: {
    display: "grid",
    gap: 3,
  },
  toggleTitle: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 950,
  },
  toggleDetail: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 750,
  },
  toggleTrack: {
    alignItems: "center",
    background: "#cbd5e1",
    borderRadius: 999,
    display: "inline-flex",
    flex: "0 0 auto",
    height: 28,
    padding: 3,
    width: 50,
  },
  toggleTrackOn: {
    background: "var(--color-primary)",
  },
  toggleKnob: {
    background: "#ffffff",
    borderRadius: 999,
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.22)",
    display: "block",
    height: 22,
    transform: "translateX(0)",
    transition: "transform 160ms ease",
    width: 22,
  },
  toggleKnobOn: {
    transform: "translateX(22px)",
  },
  primaryButton: {
    alignItems: "center",
    alignSelf: "flex-start",
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 14,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 14,
    fontWeight: 950,
    gap: 8,
    justifyContent: "center",
    padding: "12px 16px",
  },
  formSubmitButton: {
    alignSelf: "stretch",
    width: "100%",
  },
  primaryButtonPhone: {
    alignSelf: "stretch",
    marginTop: "auto",
    width: "100%",
  },
  refreshButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 12,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 900,
    padding: "11px 14px",
  },
  disabledButton: {
    cursor: "not-allowed",
    opacity: 0.55,
  },
  ghostButton: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #dbe5dd",
    borderRadius: 12,
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 900,
    gap: 8,
    padding: "10px 12px",
  },
  cardList: {
    display: "grid",
    gap: 10,
  },
  eventCard: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 18,
    display: "grid",
    gap: 12,
    gridTemplateColumns: "minmax(0, 1fr) auto",
    padding: 14,
  },
  eventCardPassed: {
    background: "#ffffff",
    opacity: 0.82,
  },
  eventMain: {
    alignItems: "start",
    display: "grid",
    gap: 12,
    gridTemplateColumns: "12px minmax(0, 1fr)",
    minWidth: 0,
  },
  statusDot: {
    borderRadius: 999,
    height: 12,
    marginTop: 6,
    width: 12,
  },
  statusDotLive: {
    background: "#22c55e",
  },
  statusDotHidden: {
    background: "#94a3b8",
  },
  statusDotPassed: {
    background: "#cbd5e1",
  },
  eventCopy: {
    minWidth: 0,
  },
  eventTitleRow: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    minWidth: 0,
  },
  eventTitle: {
    color: "#0f172a",
    fontSize: 17,
    fontWeight: 950,
    lineHeight: 1.2,
    overflowWrap: "anywhere",
  },
  passedBadge: {
    background: "#f1f5f9",
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    color: "#64748b",
    display: "inline-flex",
    fontSize: 11,
    fontWeight: 950,
    padding: "4px 8px",
    textTransform: "uppercase",
  },
  sharedBadge: {
    background: "#e8f7f1",
    border: "1px solid #bce5d6",
    borderRadius: 999,
    color: "#047857",
    display: "inline-flex",
    fontSize: 11,
    fontWeight: 950,
    padding: "4px 8px",
    textTransform: "uppercase",
  },
  eventMeta: {
    color: "var(--color-primary-dark)",
    fontSize: 13,
    fontWeight: 900,
    lineHeight: 1.35,
    marginTop: 4,
    overflowWrap: "anywhere",
  },
  eventText: {
    color: "#64748b",
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.45,
    marginTop: 4,
    overflowWrap: "anywhere",
    whiteSpace: "pre-wrap",
  },
  cardActions: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-end",
  },
  iconButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #dbe5dd",
    borderRadius: 12,
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 900,
    gap: 6,
    padding: "9px 10px",
  },
  liveButtonOn: {
    background: "#ecf7ef",
    borderColor: "#cfe8d5",
    color: "#166534",
  },
  liveButtonOff: {
    background: "#f8fafc",
    color: "#64748b",
  },
  shareButton: {
    background: "#fffaf0",
    borderColor: "#f3d99e",
    color: "#92400e",
  },
  sharedButtonOn: {
    background: "#e8f7f1",
    borderColor: "#bce5d6",
    color: "#047857",
  },
  dangerButton: {
    color: "#b42318",
  },
  disabledSoftButton: {
    cursor: "not-allowed",
    opacity: 0.5,
  },
  emptyState: {
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
    borderRadius: 16,
    color: "#64748b",
    fontSize: 14,
    fontWeight: 800,
    padding: 18,
    textAlign: "center",
  },
};

const styles = eventPageStyles;
